import { useMediaLibrary } from "@/hooks/use-media-library";
import { RefreshCw, FolderTree, Menu, ChevronRight } from "lucide-react";
import FolderSidebar from "./FolderSidebar";
import { Button } from "../ui/button";
import { ScrollArea } from "../ui/scroll-area";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger
} from "@/components/ui/sheet";
import { useState } from "react";

export function MediaSidebarPanel({
  state,
}: {
  state: ReturnType<typeof useMediaLibrary>;
  onCreateFolder?: (name: string) => Promise<void> | void;
}) {
  const {
    folderTree,
    currentFolder,
    setCurrentFolder,
    setCurrentParentId,
    fetchFolderTree,
    resetList,
  } = state;

  const [open, setOpen] = useState(false);

  // Helper to get the current folder name for the mobile button
  const currentFolderName = currentFolder?.fileName || "Root";

  const SidebarContent = (
    <div className="flex flex-col h-full bg-white">
      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-slate-200 bg-slate-50/50">
        <div className="flex items-center gap-2">
          <FolderTree className="h-4 w-4 text-slate-600" />
          <h3 className="text-sm font-semibold text-slate-900">Folders</h3>
        </div>
        <Button
          size="icon"
          variant="ghost"
          className="h-7 w-7 text-slate-500 hover:text-slate-700 hover:bg-slate-100"
          title="Refresh folders"
          onClick={() => fetchFolderTree(currentFolder)}
        >
          <RefreshCw className="h-3.5 w-3.5" />
        </Button>
      </div>

      {/* Folder Tree with Scroll */}
      <ScrollArea className="flex-1 min-h-0">
        <div className="p-3">
          <FolderSidebar
            tree={folderTree}
            currentPath={currentFolder?.fileUrl || ""}
            onSelect={(folder) => {
              setCurrentFolder(folder as any);
              setCurrentParentId(folder ? (folder as any).id : null);
              resetList(folder ? (folder as any).id : null);
              setOpen(false); // Close drawer on selection
            }}
          />
        </div>
      </ScrollArea>
    </div>
  );

  return (
    <>
      {/* MOBILE TRIGGER: Now spans full width and shows current location */}
      <div className="md:hidden flex items-center p-2 border-b bg-slate-50/50 w-full shrink-0">
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="sm" className="w-full justify-between bg-white shadow-sm border-slate-200 px-3">
              <div className="flex items-center gap-2 overflow-hidden">
                <Menu className="h-4 w-4 text-slate-500 shrink-0" />
                <span className="text-slate-400 font-normal">In:</span>
                <span className="truncate font-medium text-slate-900">{currentFolderName}</span>
              </div>
              <ChevronRight className="h-4 w-4 text-slate-400 shrink-0" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="p-0 w-72">
            <SheetHeader className="sr-only">
              <SheetTitle>Folder Navigation</SheetTitle>
            </SheetHeader>
            {SidebarContent}
          </SheetContent>
        </Sheet>
      </div>

      {/* DESKTOP SIDEBAR: Removed h-screen to allow parent to control height */}
      <aside className="hidden md:flex w-72 shrink-0 flex-col border-r border-slate-200 bg-slate-50/30 overflow-hidden">
        {SidebarContent}
      </aside>
    </>
  );
}